<script setup lang="ts">
import { onUnmounted, watch, watchEffect } from 'vue'
import { EventType, Fit, Layout, Rive, RiveFile } from '@rive-app/webgl2'
import { useWindowSize } from '@vueuse/core'
import LoadingIndicator from '@consumer/components/LoadingIndicator.vue'
import { notifyError } from '@shared/errors'
import { isProduction } from '@shared/environment'
import type { Dimensions, RiveOptions, RiveParameters } from './types'

type Props = RiveParameters & RiveOptions

/**
 * Props definition
 *
 */
const {
  loadingIndicator = true,
  loadingText = 'Loading...',
  href,
  ...props
} = defineProps<Props>()

/**
 * Emit defintions
 */
const emit = defineEmits<{ loaded: [Rive, RiveFile]; loadFailed: [any] }>()

/**
 * Template Refs
 */
const canvas = $ref<HTMLCanvasElement>()
const container = $ref<HTMLElement>()
let loading = $ref(true)
let loaded = $ref(false)
let loadFailed = $ref(false)
/**
 * Reactive variables
 */
const { width: wWidth, height: wHeight } = useWindowSize()

let riveInstance = $shallowRef<Rive>()

let dimensions = $ref<Dimensions>({
  width: 0,
  height: 0,
})

const animations = $computed(() => props.animations)

/**
 * Computed Rive Options
 */
const options = $computed(() => {
  return Object.assign({}, defaultOptions, props)
})

/**
 * Default Rive options will be overwritten if options are passed in
 */
const defaultOptions = {
  useDevicePixelRatio: true,
  fitCanvasToArtboardHeight: false,
  useOffscreenRenderer: true,
}

/**
 * Watches windowsize(height and width) and updates the canvas dimensions
 */
watchEffect(() => {
  if (canvas && container && wWidth.value && wHeight.value) {
    const { width, height } = getCanvasDimensions()

    const boundsChanged
      = width !== dimensions.width || height !== dimensions.height

    if (
      canvas
      && container
      && loaded
      && boundsChanged
    ) {
      if (options.fitCanvasToArtboardHeight)
        container.style.height = `${height}px`

      if (options.useDevicePixelRatio) {
        const dpr = 2
        canvas.width = dpr * width
        canvas.height = dpr * height
        canvas.style.width = `${width}px`
        canvas.style.height = `${height}px`
      }
      else {
        canvas.width = width
        canvas.height = height
      }
      dimensions = { width, height }
      riveInstance?.resizeDrawingSurfaceToCanvas()
    }
  }
})

/**
 * watches props.animations and updates the Rive instance
 */
watch(
  () => animations,
  () => {
    if (riveInstance && animations) {
      riveInstance.stop(riveInstance.animationNames)
      riveInstance.play(animations)
    }
  },
)

/**
 * Set up IntersectionObserver to stop rendering if the animation is not in
 * view.
 */
watchEffect(() => {
  const observer = new IntersectionObserver(([entry]) => {
    entry.isIntersecting
      ? riveInstance && riveInstance.play()
      : riveInstance && riveInstance.pause()
  })

  if (canvas)
    observer.observe(canvas)

  return () => {
    observer.disconnect()
  }
})

/**
 * gets dimensions of container returns width and height
 */
function getCanvasDimensions () {
  const { width, height }
    = container?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0)
  if (riveInstance && options.fitCanvasToArtboardHeight) {
    const { maxY, maxX } = riveInstance.bounds
    return { width, height: width * (maxY / maxX) }
  }
  return { width, height }
}

function onTouchEnd () {
  if (!href) return

  window.location.href = href
}

/**
 * onMounted initializes the Rive instance
 */
watch(
  () => [canvas, props.src],
  () => {
    if (riveInstance) {
      riveInstance.stopRendering()
      riveInstance = undefined
    }

    if (canvas && props.src) {
      const { useOffscreenRenderer } = options
      loading = true
      loadFailed = false
      const { src, ...restProps } = props

      const layout = props.layout || new Layout({ fit: Fit.Contain })

      if (restProps.riveFile) {
        const rawFile = toRaw(restProps.riveFile)
        const r = new Rive({
          ...restProps,
          riveFile: rawFile,
          layout,
          useOffscreenRenderer,
          canvas,
        })
        r.on(EventType.Load, () => {
          emit('loaded', r, props.riveFile!)
          riveInstance = r
          loaded = true
          setTimeout(() => {
            // allow some time for the first frame to render
            // before removing the loading state
            loading = false
          }, 100)
        })
        r.on(EventType.LoadError, (error) => {
          emit('loadFailed', error)
          setTimeout(() => {
            // allow some time for the first frame to render
            // before removing the loading state
            loading = false
          }, 100)
          loadFailed = true
          // Rive is very flaky with errors that are causing circle tests to fail randomly.
          if (isProduction) notifyError(error)
        })
      }
      else {
        const file = new RiveFile({
          src,
          onLoad: (e) => {
            const loadedFile = e.data as RiveFile
            const r = new Rive({
              ...restProps,
              riveFile: loadedFile,
              layout,
              useOffscreenRenderer,
              canvas,
            })
            r.on(EventType.Load, () => {
              emit('loaded', r, loadedFile)
              riveInstance = r
              setTimeout(() => {
                // allow some time for the first frame to render
                // before removing the loading state
                loading = false
              }, 100)
              loaded = true
            })
          },
        })
        file.init().catch((error) => {
          emit('loadFailed', error)
          setTimeout(() => {
            // allow some time for the first frame to render
            // before removing the loading state
            loading = false
          }, 100)
          loadFailed = true
          // Rive is very flaky with errors that are causing circle tests to fail randomly.
          if (isProduction) notifyError(error)
        })
      }
    }
  },
)

onUnmounted(() => {
  if (riveInstance) {
    riveInstance.stopRendering()
    riveInstance = undefined
  }
})

defineExpose({
  riveInstance,
})
</script>

<template>
  <section ref="container" class="relative w-full h-full">
    <div
      v-show="loading || loadFailed"
      class="absolute w-full h-full flex justify-center items-center flex-col gap-2"
    >
      <!-- What we show while rive is loading -->
      <slot name="loading" :loading="loading">
        <template v-if="loading && loadingIndicator">
          <LoadingIndicator/>
          <span>{{ loadingText }}</span>
        </template>
      </slot>

      <!-- What we show when rive fails -->
      <slot name="loadFailed" :loadFailed="loadFailed"/>
      <div v-if="loadFailed && !$slots.loadFailed" class="flex flex-col gap-1 items-center justify-center">
        <div class="font-bold text-xl">Ooops! something went wrong</div>
        <div class="text-center text-sm text-navy">Our support team has been notified</div>
        <slot name="loadFailedAction"/>
      </div>
    </div>

    <a v-if="href" :href="href" class="w-full h-full absolute top-0 left-0" @touchend="onTouchEnd">
      <canvas ref="canvas" style="vertical-align: top;" class="w-full h-full"/>
    </a>
    <canvas v-else ref="canvas" style="vertical-align: top;" class="w-full h-full"/>
  </section>
</template>
